import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@/components/icons";
import CloseButton from '@/components/CloseButton';

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
      className={cn(
      "fixed inset-0 z-40 bg-transparent backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Manage `modal-open` class on the document to blur the main content when any dialog overlay is mounted.
const DialogOverlayWithBlur = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  React.useEffect(() => {
    try {
      const root = document.documentElement;
      const current = Number(root.getAttribute('data-modal-count') || '0');
      const next = current + 1;
      root.setAttribute('data-modal-count', String(next));
      root.classList.add('modal-open');
      return () => {
        const cur = Number(root.getAttribute('data-modal-count') || '1');
        const newCount = Math.max(0, cur - 1);
        if (newCount <= 0) {
          root.removeAttribute('data-modal-count');
          root.classList.remove('modal-open');
        } else {
          root.setAttribute('data-modal-count', String(newCount));
        }
      };
    } catch (e) {
      // noop (SSR or restricted env)
    }
    return () => {};
  }, []);

  return (
      <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-transparent backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});
DialogOverlayWithBlur.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideClose?: boolean;
  fullScreen?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose, fullScreen, ...props }, ref) => {
  const defaultStyle = { WebkitOverflowScrolling: 'touch' } as React.CSSProperties;
  const mergedStyle = { ...(props as any).style || {}, ...defaultStyle } as React.CSSProperties;
  
  const [navbarHeight, setNavbarHeight] = React.useState(0);
  
  React.useEffect(() => {
    const measureNavbar = () => {
      const navbar = document.querySelector('.app-header');
      if (navbar) {
        const rect = navbar.getBoundingClientRect();
        const safeArea = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0px';
        const safeAreaValue = parseInt(safeArea) || 0;
        setNavbarHeight(rect.height + safeAreaValue);
      }
    };
    
    measureNavbar();
    globalThis.addEventListener('resize', measureNavbar);
    return () => globalThis.removeEventListener('resize', measureNavbar);
  }, []);
  
  return (
    <DialogPortal>
      <DialogOverlayWithBlur />
      <DialogPrimitive.Content
        ref={ref}
        style={{
          ...mergedStyle, 
          maxHeight: navbarHeight > 0 ? `calc(100vh - ${navbarHeight}px - 0.5rem)` : undefined 
        }}
        className={cn(
            fullScreen
            ? "fixed inset-0 z-[10000] overflow-y-auto"
            : "fixed left-[50%] top-2 sm:top-[50%] z-[10000] grid w-full max-w-lg sm:max-h-[80vh] translate-x-[-50%] sm:translate-y-[-50%] translate-y-0 gap-4 border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] modal-content overflow-y-auto",
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close asChild>
            <CloseButton ariaLabel="Cerrar" className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pb-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlayWithBlur as DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
